import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
	DatePlacementDto,
	DatePlacementRes,
	SignaturePlacementDto,
	SignatureSlotRes,
} from '@dealstack/shared/lib/generated';
import type { PlacementAdapter } from '@dealstack/frontend/shared/components/FieldBinding/placementAdapter.ts';
import { dealPlacementAdapter } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/dealPlacementAdapter.ts';
import type { CanvasPlacement, PdfBox } from './PlacementCanvas/PlacementCanvas.types.ts';
import { signatureSlotKey } from '@dealstack/shared/lib/common/business/signatureSlotKey';
import { dateKey, expectedAspectRatio, parseDateKey } from './PlacementCanvas/placement.utils.ts';
import { findOverlappingKeys } from './PlacementCanvas/placementCollision.utils.ts';
import {
	buildDateMetaMap,
	buildSlotMetaMap,
	defaultBoxFor,
	defaultDateBoxFor,
	isPlacementSetComplete,
	needsRescale,
	rescalePlacement,
} from './signaturePlacementShared.ts';
import type { DateBox, SignaturePlacementResult } from './useSignaturePlacement.types.ts';

const toCanvasPlacement = (slot: SignatureSlotRes): CanvasPlacement | null => {
	if (!slot.placement) {
		return null;
	}
	return {
		pageNum: slot.placement.pageNum,
		pdfBox: {
			x: slot.placement.x,
			y: slot.placement.y,
			width: slot.placement.width,
			height: slot.placement.height,
		},
	};
};

const buildServerPlacementMap = (slots: SignatureSlotRes[]): Map<string, CanvasPlacement> => {
	const map = new Map<string, CanvasPlacement>();
	slots.forEach((slot) => {
		const canvas = toCanvasPlacement(slot);
		if (canvas) {
			map.set(signatureSlotKey(slot), canvas);
		}
	});
	return map;
};

const toDto = (
	slots: SignatureSlotRes[],
	placementsByKey: Map<string, CanvasPlacement>
): SignaturePlacementDto[] => {
	const out: SignaturePlacementDto[] = [];
	slots.forEach((slot) => {
		const key = signatureSlotKey(slot);
		const placement = placementsByKey.get(key);
		if (!placement) {
			return;
		}
		out.push({
			signingRoleId: slot.signingRoleId,
			isWitness: slot.isWitness,
			signatureIndex: slot.signatureIndex,
			pageNum: placement.pageNum,
			x: placement.pdfBox.x,
			y: placement.pdfBox.y,
			width: placement.pdfBox.width,
			height: placement.pdfBox.height,
		});
	});
	return out;
};

/** Skips coordinate-less rows (a Word master's), which the canvas would position off a NaN. */
const seedDateBoxes = (rows: DatePlacementRes[]): DateBox[] =>
	[...rows]
		.sort((a, b) => a.placementIndex - b.placementIndex)
		.flatMap((row) => {
			const { pageNum, x, y } = row;
			if (pageNum === null || x === null || y === null) {
				return [];
			}
			return [
				{ id: row.id, pageNum, pdfBox: { x, y, width: row.width, height: row.height } },
			];
		});

export const useSignaturePlacement = (
	templateId: string | null,
	showMetadata: boolean,
	/** Where the slots come from. Defaults to the deal's, the only owner before KYC had one. */
	adapter: PlacementAdapter = dealPlacementAdapter
): SignaturePlacementResult => {
	const { data: slotsData, isLoading, isError } = adapter.useSlots(templateId);
	const slots = useMemo<SignatureSlotRes[]>(() => slotsData?.slots ?? [], [slotsData]);
	const requiresPostDating = slotsData?.requiresPostDating ?? false;
	const serverDatePlacements = useMemo(() => slotsData?.datePlacements ?? [], [slotsData]);

	const serverPlacementsByKey = useMemo(() => buildServerPlacementMap(slots), [slots]);

	const [localOverrides, setLocalOverrides] = useState<Map<string, CanvasPlacement>>(new Map());
	const [removedKeys, setRemovedKeys] = useState<Set<string>>(new Set());
	const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);

	// Date boxes need their own ordered list, not the override/tombstone model above: removal
	// must re-index to stay contiguous, and each box carries its row `id` through the renumber.
	const [dateBoxes, setDateBoxes] = useState<DateBox[] | null>(null);

	// Lets "seeded but untouched" be told from "edited since" by reference. State, not refs:
	// StrictMode rolls state back between its two render passes but keeps ref mutations, so a
	// ref guard would make the second pass skip the seed and silently drop the saved rows.
	const [seedBaseline, setSeedBaseline] = useState<{
		rows: DatePlacementRes[];
		boxes: DateBox[];
	} | null>(null);

	// Re-seed only while the working list is untouched, so a refetch can't clobber edits.
	// `slotsData &&` is load-bearing: before the query resolves serverDatePlacements is the
	// memoised empty array, indistinguishable from a template with no date boxes.
	const isDateListPristine = dateBoxes === null || dateBoxes === seedBaseline?.boxes;
	if (slotsData && isDateListPristine && seedBaseline?.rows !== serverDatePlacements) {
		const seeded = seedDateBoxes(serverDatePlacements);
		setSeedBaseline({ rows: serverDatePlacements, boxes: seeded });
		setDateBoxes(seeded);
	}

	const resolvedDateBoxes = dateBoxes ?? [];
	// Hydration alone (null to a seeded array) must not flip isDirty for an untouched document.
	const dateBoxesDirty = dateBoxes !== null && dateBoxes !== seedBaseline?.boxes;

	const placementsByKey = useMemo(() => {
		const merged = new Map(serverPlacementsByKey);
		removedKeys.forEach((k) => merged.delete(k));
		localOverrides.forEach((value, key) => {
			merged.set(key, value);
		});
		resolvedDateBoxes.forEach((box, index) => merged.set(dateKey(index), box));
		return merged;
	}, [serverPlacementsByKey, removedKeys, localOverrides, resolvedDateBoxes]);

	const datePlacementKeys = useMemo(
		() => resolvedDateBoxes.map((_, index) => dateKey(index)),
		[resolvedDateBoxes]
	);

	const slotByKey = useMemo(() => {
		const merged = buildSlotMetaMap(slots, (slot) => signatureSlotKey(slot));
		buildDateMetaMap(datePlacementKeys).forEach((value, key) => merged.set(key, value));
		return merged;
	}, [slots, datePlacementKeys]);

	const onPlacementChange = useCallback((key: string, pageNum: number, pdfBox: PdfBox) => {
		const dateIndex = parseDateKey(key);
		if (dateIndex !== null) {
			// Spread, not replace: dropping `id` here would turn a moved box into a new row.
			setDateBoxes((prev) =>
				(prev ?? []).map((box, index) =>
					index === dateIndex ? { ...box, pageNum, pdfBox } : box
				)
			);
			return;
		}
		setLocalOverrides((prev) => {
			const next = new Map(prev);
			next.set(key, { pageNum, pdfBox });
			return next;
		});
		// Re-placing a removed key un-tombstones it.
		setRemovedKeys((prev) => {
			if (!prev.has(key)) {
				return prev;
			}
			const next = new Set(prev);
			next.delete(key);
			return next;
		});
	}, []);

	// Drop the slot at a default near the page top, offsetting each so they
	// don't stack; height tracks showMetadata so it matches the renderer.
	const onPlaceAtDefault = useCallback(
		(key: string, pageNum: number) => {
			const alreadyPlaced = placementsByKey.size;
			const offset = alreadyPlaced * 20;
			setLocalOverrides((prev) => {
				const next = new Map(prev);
				next.set(key, {
					pageNum,
					pdfBox: defaultBoxFor(showMetadata, offset, slotByKey.get(key)?.isWitness),
				});
				return next;
			});
			setRemovedKeys((prev) => {
				if (!prev.has(key)) {
					return prev;
				}
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
			setSelectedSlotKey(key);
		},
		[placementsByKey, showMetadata, slotByKey]
	);

	// Offset de-stacks the new box from any already-placed ones.
	const onAddDateField = useCallback(
		(pageNum: number) => {
			setDateBoxes((prev) => {
				const current = prev ?? [];
				const offset = current.length * 20;
				return [...current, { pageNum, pdfBox: defaultDateBoxFor(showMetadata, offset) }];
			});
		},
		[showMetadata]
	);

	const onRemove = useCallback((key: string) => {
		const dateIndex = parseDateKey(key);
		if (dateIndex !== null) {
			// Splice, not delete-by-key: the backend validator rejects gaps in the index.
			setDateBoxes((prev) => (prev ?? []).filter((_, index) => index !== dateIndex));
			setSelectedSlotKey((current) => (current === key ? null : current));
			return;
		}
		setRemovedKeys((prev) => {
			const next = new Set(prev);
			next.add(key);
			return next;
		});
		setLocalOverrides((prev) => {
			if (!prev.has(key)) {
				return prev;
			}
			const next = new Map(prev);
			next.delete(key);
			return next;
		});
		setSelectedSlotKey((current) => (current === key ? null : current));
	}, []);

	// Rescale to the current showMetadata ratio: everything on a toggle flip, only mis-ratio
	// geometry on load. handledServerSetRef guards against an infinite re-override loop; the
	// date arm is self-terminating instead, since a rescaled box no longer needs a rescale.
	const prevShowMetadataRef = useRef<boolean | null>(null);
	const handledServerSetRef = useRef<Map<string, CanvasPlacement> | null>(null);
	useEffect(() => {
		// Nothing to rescale yet, and a host seeding the saved mode from this same response
		// would otherwise read as a flip and dirty an untouched template.
		if (!slotsData) {
			return;
		}
		// Per slot, not per template: a witness box is locked to a taller ratio than its
		// party box, so the two can't share one target.
		const ratioFor = (key: string): number =>
			expectedAspectRatio(showMetadata, slotByKey.get(key)?.isWitness ?? false);

		const isFirstObservation = prevShowMetadataRef.current === null;
		const flipped = !isFirstObservation && prevShowMetadataRef.current !== showMetadata;
		prevShowMetadataRef.current = showMetadata;

		// Flip: rescale every merged placement bar the date boxes, which live in their own
		// state and are handled below. Load: only mis-ratio server rows, and only when the
		// memoised server Map identity has changed — that branch is what migrates witness
		// boxes saved before they had their own ratio.
		let sourceEntries: Array<[string, CanvasPlacement]>;
		if (flipped) {
			sourceEntries = Array.from(placementsByKey.entries()).filter(
				([key]) => parseDateKey(key) === null
			);
		} else if (handledServerSetRef.current !== serverPlacementsByKey) {
			handledServerSetRef.current = serverPlacementsByKey;
			sourceEntries = Array.from(serverPlacementsByKey.entries()).filter(([key, value]) =>
				needsRescale(value, ratioFor(key))
			);
		} else {
			sourceEntries = [];
		}

		if (sourceEntries.length > 0) {
			// Lift into overrides so the derived DTO list (and Save) sees the new geometry.
			setLocalOverrides((prev) => {
				const next = new Map(prev);
				sourceEntries.forEach(([key, value]) => {
					next.set(key, rescalePlacement(value, ratioFor(key)));
				});
				return next;
			});
		}

		// Date boxes get the same treatment, on load as well as on flip. Load matters because
		// a template can hold date geometry that disagrees with its own showStampMetadata, and
		// a date box was the one piece of the payload that never self-healed: the signature
		// rows above migrated, it did not, and every save 400'd on it (DC-1163).
		const dateRatioFor = (index: number): number => ratioFor(dateKey(index));
		const rescaleDates = flipped
			? resolvedDateBoxes.length > 0
			: // `width > 0` keeps this arm terminating. `rescalePlacement` derives the height from
				// the width, so a zero/negative width rescales to a box `needsRescale` still rejects
				// and the effect would re-run forever. The signature arm above is ref-guarded instead.
				resolvedDateBoxes.some(
					(box, index) => box.pdfBox.width > 0 && needsRescale(box, dateRatioFor(index))
				);

		if (rescaleDates) {
			// Spread the original box back over the rescaled geometry to keep `id`.
			// A date box is never a witness, so ratioFor routes it to the party ratio.
			setDateBoxes((prev) =>
				(prev ?? []).map((box, index) => ({
					...box,
					...rescalePlacement(box, dateRatioFor(index)),
				}))
			);
		}
	}, [
		slotsData,
		showMetadata,
		placementsByKey,
		serverPlacementsByKey,
		resolvedDateBoxes,
		slotByKey,
	]);

	const isComplete = useMemo(
		() => isPlacementSetComplete(slots, (slot) => signatureSlotKey(slot), placementsByKey),
		[slots, placementsByKey]
	);

	const hasOverlappingPlacements = useMemo(
		() => findOverlappingKeys(placementsByKey, []).size > 0,
		[placementsByKey]
	);

	const isDirty = localOverrides.size > 0 || removedKeys.size > 0 || dateBoxesDirty;

	const signaturePlacements = useMemo(
		() => toDto(slots, placementsByKey),
		[slots, placementsByKey]
	);

	const datePlacements = useMemo<DatePlacementDto[]>(
		() =>
			resolvedDateBoxes.map((box, index) => ({
				// Undefined for a box the author just drew — the backend inserts those.
				id: box.id,
				placementIndex: index,
				pageNum: box.pageNum,
				x: box.pdfBox.x,
				y: box.pdfBox.y,
				width: box.pdfBox.width,
				height: box.pdfBox.height,
			})),
		[resolvedDateBoxes]
	);

	return {
		slots,
		isLoading,
		isError,
		placementsByKey,
		slotByKey,
		selectedSlotKey,
		setSelectedSlotKey,
		onPlacementChange,
		onPlaceAtDefault,
		onRemove,
		isComplete,
		hasOverlappingPlacements,
		isDirty,
		signaturePlacements,
		datePlacementKeys,
		onAddDateField,
		datePlacements,
		requiresPostDating,
	};
};
